/**
 * Les PRÉFÉRENCES d'un utilisateur, telles que l'écran les écrit — et leur fusion
 * optimiste, avant que le serveur n'ait répondu.
 *
 * ⚠️ **Ce fichier est le MIROIR que lit le serveur** :
 * `backend/tests/unit/test_preferences_ecran_et_serveur_saccordent.py` y relève
 * chaque `interface` et exige que le modèle Pydantic en connaisse tous les blocs
 * et toutes les clés. Une clé que l'écran écrit sans que le serveur la déclare
 * n'est pas ignorée : elle est EFFACÉE au premier enregistrement.
 *
 * Sorti de `AuthContext.tsx` (règle des 300 lignes), sans rien changer à la
 * forme des préférences ni à leur fusion.
 */

export interface GlobalPreferences {
    sidebar_state: 'expanded' | 'collapsed'
    color_scheme: 'argalis' | 'neutral'
    theme: 'light' | 'dark' | 'system'
    language: 'fr' | 'en'
    reduce_motion: boolean
    high_contrast: boolean
    large_text: boolean
    dyslexia_font: boolean
}

export interface GridPreferences {
    columns: string[]
    hidden_columns: string[]
    column_widths: Record<string, number>
    sort_by: string | null
    sort_direction: 'asc' | 'desc' | null
    page_size: number
    columns_pinned: { left: string[]; right: string[] }
    density: 'compact' | 'normal' | 'comfortable'
    active_profile: string
    profiles: Record<string, Record<string, unknown>>
    aggregation_column?: string | null
}

/**
 * Disposition d'UN tableau de bord — miroir de `DashboardLayoutPreferences`.
 *
 * Le catalogue des tuiles vit dans le REGISTRE (il porte du JSX) ; ce bloc ne
 * porte que le choix de l'utilisateur. Une tuile absente d'`order` est une
 * tuile jamais vue : elle prend sa place canonique, pas la fin de la liste.
 *
 * ⚠️ `known` n'est pas une redite d'`order` — il distingue « décochée par
 * l'utilisateur » de « livrée après son dernier passage ».
 */
export interface DashboardLayoutPreferences {
    order?: string[]
    hidden?: string[]
    known?: string[]
    density?: 'compact' | 'comfortable' | 'custom'
    /** Surcharges par tuile — `{tileId: {option: valeur}}`, défauts dans le registre. */
    options?: Record<string, Record<string, unknown>>
    /** Période, fenêtre comparée, commercial, agrégation — la mémoire du retour. */
    filters?: Record<string, unknown>
}

export interface GedPreferences {
    sort_field: string
    sort_direction: 'asc' | 'desc'
    status_filter: string | null
    expanded_folders: string[]
    view_mode: 'list' | 'grid'
}

export interface TaskCalendarPreferences {
    task_view?: 'list' | 'calendar'
    calendar_view?: 'month' | 'week' | 'day' | null
}

export interface NotificationFiltersPreferences {
    overdue?: boolean
    urgent?: boolean
}

export interface CvDisplayPreferences {
    include_photo?: boolean
    include_bio?: boolean
    include_capabilities?: boolean
    capabilities_granularity?: 'sector' | 'family' | 'group' | 'product'
    include_requirements?: boolean
    include_rich_blocks?: boolean
    history_years?: number
}

export interface QuoteDisplayPreferences {
    options?: Record<string, boolean | string>
    language?: 'fr' | 'en'
}

export interface SalesPreferences {
    // SALE-020 §0 — `DASHBOARD` rejoint l'enum : le tableau de bord devient une
    // vue de démarrage mémorisable. Le repli est PLAN-AWARE côté écran (une
    // préférence posée sous un plan supérieur retombe sur la liste).
    quotes_default_view?: 'TABLE' | 'KANBAN' | 'DASHBOARD'
    quotes_last_board_id?: string | null
    // TND-004/005 — les mêmes deux préférences pour les appels d'offres. Le
    // bloc `sales` est fusionné clé à clé (plus bas) : mémoriser le board
    // consulté n'efface donc pas la vue par défaut, ni celle des devis.
    // TND-021 — `DASHBOARD` comme pour les devis : le tableau de bord est un
    // point d'atterrissage à part entière, pas un onglet secondaire.
    tenders_default_view?: 'TABLE' | 'KANBAN' | 'DASHBOARD'
    tenders_last_board_id?: string | null
    /**
     * SALE-026 — le bulletin se lit par BARÈME ou par AFFAIRE.
     *
     * Aucun des deux n'est bon pour tout le monde : quelqu'un qui n'a qu'une
     * seule grille ne veut pas déplier un groupe unique pour voir ses affaires,
     * alors qu'un plan à quatre barèmes ne se lit qu'en commençant par ce qui
     * paie. Le choix se mémorise donc plutôt que de s'imposer (PO, 2026-08-13).
     */
    commissions_group_by?: 'RULE' | 'DEAL'
}

export interface WorkflowsPreferences {
    assistant_seen?: boolean
    assistant_dismissed_at?: string | null
}

export interface UserPreferences {
    global: GlobalPreferences
    grids: Record<string, GridPreferences>
    /** Un tableau de bord par clé (`sales-quotes`, puis sessions, factures…). */
    dashboards?: Record<string, DashboardLayoutPreferences>
    ged?: GedPreferences
    title_usage?: Record<string, number> // ADMIN-012: compteurs fréquence titres d'appellation
    task_calendar?: TaskCalendarPreferences // WKF-013 §7.2bis: vue Liste/Calendrier + Mois/Semaine/Jour
    notification_filters?: NotificationFiltersPreferences // WKF-013 §7.2: filtres En retard / Urgentes
    recent_assignees?: string[] // WKF-013 §7.4: derniers destinataires de transfert (MRU)
    cv_display?: CvDisplayPreferences // INSTR-010 V4: options d'affichage du CV mémorisées
    quote_display?: QuoteDisplayPreferences // SALE-010 §5.7: options d'affichage du devis mémorisées
    sales?: SalesPreferences // SALE-009 §4.1: vue Table/Kanban par défaut + dernier board consulté
    workflows?: WorkflowsPreferences // WKF-014 §5.1: assistant déjà vu (ouverture auto une seule fois)
}

export type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * La fusion optimiste d'un `patch` dans les préférences COURANTES — ce que
 * `AuthContext.updatePreferences` affiche avant la réponse du serveur. Elle doit
 * suivre le régime du serveur (`KEYED_MERGE_BLOCKS`, blocs fusionnés clé à clé),
 * sinon l'état affiché diverge de ce qui est enregistré.
 */
export function fusionnerLesPreferences(
    actuelles: UserPreferences | undefined,
    patch: DeepPartial<UserPreferences>,
): UserPreferences {
    const newPrefs = { ...actuelles } as UserPreferences
    if (patch.global) {
        newPrefs.global = { ...newPrefs.global, ...patch.global }
    }
    if (patch.grids) {
        newPrefs.grids = { ...newPrefs.grids }
        Object.keys(patch.grids).forEach(k => {
            // @ts-ignore
            newPrefs.grids[k] = { ...newPrefs.grids[k], ...patch.grids[k] }
        })
    }
    // Merge PAR IDENTIFIANT, comme `grids` : régler le tableau de bord
    // Devis ne doit jamais emporter celui des Sessions. Le serveur
    // applique le même régime (`KEYED_MERGE_BLOCKS`) — sans cette
    // branche, l'état optimiste divergerait de ce qui est enregistré,
    // et les tuiles masquées « reviendraient » jusqu'au rechargement.
    if (patch.dashboards) {
        newPrefs.dashboards = { ...newPrefs.dashboards }
        Object.keys(patch.dashboards).forEach(k => {
            // @ts-ignore
            newPrefs.dashboards[k] = { ...newPrefs.dashboards?.[k], ...patch.dashboards[k] }
        })
    }
    if (patch.ged) {
        newPrefs.ged = { ...newPrefs.ged, ...patch.ged } as GedPreferences
    }
    if (patch.title_usage) {
        newPrefs.title_usage = { ...newPrefs.title_usage, ...patch.title_usage }
    }
    if (patch.task_calendar) {
        newPrefs.task_calendar = { ...newPrefs.task_calendar, ...patch.task_calendar }
    }
    if (patch.notification_filters) {
        newPrefs.notification_filters = {
            ...newPrefs.notification_filters,
            ...patch.notification_filters,
        }
    }
    // Liste MRU : remplacement en bloc (pas de deep-merge d'un tableau).
    if (patch.recent_assignees) {
        newPrefs.recent_assignees = patch.recent_assignees
    }
    if (patch.cv_display) {
        newPrefs.cv_display = { ...newPrefs.cv_display, ...patch.cv_display }
    }
    if (patch.quote_display) {
        newPrefs.quote_display = { ...newPrefs.quote_display, ...patch.quote_display }
    }
    // SALE-009 §5.2 : deep-merge par clé — écrire `quotes_default_view`
    // seul ne doit jamais écraser `quotes_last_board_id` (et inversement).
    if (patch.sales) {
        newPrefs.sales = { ...newPrefs.sales, ...patch.sales }
    }
    return newPrefs
}
